import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, MapPin } from "lucide-react";

import { GOOGLE_MAPS_API_KEY } from "../../utils/googleMaps";
import { loadGoogleMaps } from "../../utils/loadGoogleMaps";
import { placeToParams } from "../../utils/addressSearch";
import { cn } from "../../lib/utils";
import { ARROW_NUDGE } from "../../constants/linkStyle";
import { track } from "../../utils/track";

/**
 * The one address field, wherever a search can start.
 *
 * The marketing pages used to show a field that was really a link into `/app`,
 * on the reasoning that geocoding needs the Maps key and only the app bundle
 * had it. That was wrong about the key — `REACT_APP_GOOGLE_MAPS_API_KEY` is a
 * single build-time inline available to every chunk — and right only about the
 * SDK, which this component loads on demand.
 *
 * The suggestion list is ours, not Google's. `places.SearchBox` and
 * `Autocomplete` both inject a `.pac-container` into `document.body` carrying
 * their own type, spacing and iconography; it can be CSS-patched but not
 * restructured, and it read as a Google widget dropped onto a page that looks
 * like nothing else. `AutocompleteService` returns predictions as data
 * instead, so the list below is the site's own — and when the Protomaps/Photon
 * migration lands, only the two service calls change rather than a
 * third-party widget being torn out of the markup.
 *
 * "Powered by Google" stays. The Places terms require the attribution wherever
 * predictions are shown without a Google map, which is exactly this case.
 *
 * Two ways to commit, both ending in the same `?lat&lng&loc` and the same
 * destination: choose a prediction, or type an address and submit and we
 * geocode the text. The zero state only ever had the first, and its button
 * could do nothing but refocus the field.
 */

/** Which surface a search started on, as the event log names it (FRS FR2). */
const SURFACE = {
  marketing: "marketing_hero",
  app: "app_search",
  console: "console_search",
};

/** The two-letter state from a geocoder result — GA's `region`, nothing finer. */
const stateOf = (place) => {
  const code = place?.address_components?.find((c) =>
    c.types?.includes("administrative_area_level_1"),
  )?.short_name;
  return /^[A-Z]{2}$/.test(code || "") ? code : undefined;
};

/**
 * The notice at the point of search (behaviour-logging task 12.5, TR §7, R5).
 *
 * DRAFT, pending counsel (task 12.3 — counsel item 5 asks whether this line is
 * enough notice-at-collection for visitors who never open the policy). The
 * console's searched-address lookups write a `lookup_served` row whether or
 * not the visitor accepted analytics cookies, so the line sits beside every
 * field a search can start from. Shortened from TR §7's three-sentence draft:
 * its "temporary session identifier" clause is dropped because the id is kept
 * across visits once analytics cookies are accepted (`getSessionId`), and the
 * policy section it links to says so properly.
 *
 * Not blocking and no checkbox: notice, not consent.
 */
export const SEARCH_NOTICE =
  "We keep a record of addresses searched and the results shown, for support and accuracy checks.";
export const SEARCH_NOTICE_HREF = "/privacy#search-history";

/** US-only: SDWIS is a US federal dataset, so a match elsewhere has no answer behind it. */
const US_ONLY = { componentRestrictions: { country: "us" } };

/** Long enough that typing a street name is one request, not ten. */
const DEBOUNCE_MS = 220;

/** Below this, predictions are noise and every keystroke is a billed request. */
const MIN_QUERY = 3;

/**
 * Per-surface chrome. Three surfaces, one behaviour — the suggestion list, the
 * keyboard handling, the debounce and the two ways to commit are shared, and
 * only the dressing differs.
 *
 * Kept as a table rather than as ternaries in the markup: at two variants an
 * inline `onMarketing ? a : b` was readable, at three it would be nine of them
 * threaded through one form.
 */
const CHROME = {
  marketing: {
    wrap: "max-w-[600px]",
    form: "rounded-lg bg-white shadow-sm border",
    icon: "left-4 h-4 w-4 text-zp-slate",
    input:
      "py-4 pl-11 pr-4 text-[15px] text-zp-ink placeholder:text-zp-slate sm:text-[17px]",
    button: "px-4 sm:px-5",
    /* Below `sm` the hero's field gives the label's width to the input: the
       arrow still says the button submits. */
    label: "hidden sm:inline",
    placeholder: "Enter your street address",
    error: "text-white/70",
    // The hero and the article CTAs are all dark boxes.
    notice: "mt-2.5 text-white/55",
    noticeLink: "text-white/80 hover:text-white",
  },
  app: {
    wrap: "max-w-xl",
    form: "rounded-lg border border-zp-ink/[0.12] bg-white dark:border-border dark:bg-card",
    icon: "left-4 h-4 w-4 text-muted-foreground",
    input: "h-14 pl-11 pr-4 text-[15px] text-foreground placeholder:text-muted-foreground",
    button: "px-4 sm:px-5",
    label: undefined,
    placeholder: "Enter a street address",
    error: "text-muted-foreground",
    notice: "mt-2.5 text-zp-slate dark:text-muted-foreground",
    noticeLink: "text-zp-ink hover:text-zp-link dark:text-foreground",
  },
  /**
   * The console header. Same object as the hero's, built to the bar's scale:
   * `rounded-md` for the console's 6px radius, a 32px field to match the Save
   * and Export buttons it sits between, and the action reduced to its arrow —
   * spelled out, a third dark button on that row would compete with the two
   * that act on the result rather than on the address.
   *
   * Width is the caller's: the bar decides how much of itself to give this.
   */
  console: {
    wrap: "max-w-none",
    form: "rounded-md border border-zp-ink/[0.12] bg-white dark:border-border dark:bg-card",
    icon: "left-2.5 h-3.5 w-3.5 text-muted-foreground",
    input: "h-8 pl-8 pr-2.5 text-[13px] text-foreground placeholder:text-muted-foreground",
    button: "px-2.5",
    label: "sr-only",
    placeholder: "Search an address",
    error: "text-muted-foreground",
    /* A permanent second line would grow the bar every pane hangs from, so
       here the notice drops under the field only while the search has focus —
       which is when someone is about to search. `invisible` rather than
       unmounted: the input's `aria-describedby` still reaches hidden text, so
       a screen reader hears it on focus, and a hidden link cannot be tabbed
       to while it is not shown. The suggestion list (z-40) covers it while
       predictions are open. */
    notice: cn(
      "invisible absolute left-0 right-0 top-full z-30 mt-1 rounded-md border px-2.5 py-1.5 shadow-sm",
      "border-zp-ink/[0.12] bg-white text-zp-slate dark:border-border dark:bg-card dark:text-muted-foreground",
      "group-focus-within/lookup:visible",
    ),
    noticeLink: "text-zp-ink hover:text-zp-link dark:text-foreground",
  },
};

const AddressLookup = ({
  variant = "app",
  action = "Check water",
  autoFocus = false,
  /**
   * Seeds the field, and keeps it in step with an address that changed from
   * somewhere else — a nearby read, the back button. The console's bar is the
   * only place the current address is written on that surface, so a field that
   * stayed empty or went stale would leave the page unlabelled.
   */
  initialValue = "",
  /**
   * Replaces the default navigate-on-commit.
   *
   * The console needs this: by default a commit navigates to `/app?…`, which
   * would remount the page and drop the reader's place in the console. The
   * console passes its own handler and updates its params in place.
   */
  onCommit,
  /**
   * Wait for a focus before loading the Maps SDK. On for the marketing pages,
   * where the field is decoration until someone uses it; off on `/app`, whose
   * entire purpose is this field and which loads the SDK for its map anyway.
   *
   * Not only a bundle question — the script is a billable third-party request,
   * and paying for every homepage view when almost nobody searches is how a
   * free marketing page becomes an invoice.
   */
  deferred = false,
  /**
   * The one-line notice that searches are recorded, linking to the privacy
   * policy (`SEARCH_NOTICE`). On wherever a search can start; off only for a
   * caller that shows the same notice itself.
   */
  notice = true,
  className,
}) => {
  const navigate = useNavigate();

  const listId = useId();
  const noticeId = useId();
  const inputRef = useRef(null);
  const servicesRef = useRef(null);
  const alive = useRef(true);
  const requestSeq = useRef(0);

  const [value, setValue] = useState(initialValue);
  const [armed, setArmed] = useState(!deferred);
  const [predictions, setPredictions] = useState([]);
  const [active, setActive] = useState(-1);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState(null);

  const onMarketing = variant === "marketing";
  const chrome = CHROME[variant] ?? CHROME.app;

  /* Follow an address that changed elsewhere. Guarded on the prop rather than
     run every render: this must not fight a reader who is mid-edit, and
     `initialValue` only changes when a new address has actually been
     committed. */
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  /* Set on mount as well as cleared on unmount. StrictMode runs effects
     mount → cleanup → mount in development, and a ref initialiser runs only
     once: without the assignment here, the first cleanup would leave this
     false for the rest of the component's life and every guarded callback
     below would bail. The field worked in production and was inert in
     development, which is the worst way round. */
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // Arming loads the SDK and builds the two services. Nothing before that.
  useEffect(() => {
    if (!armed || servicesRef.current) return;
    loadGoogleMaps()
      .then((maps) => {
        if (!alive.current || servicesRef.current) return;
        servicesRef.current = {
          autocomplete: new maps.places.AutocompleteService(),
          geocoder: new maps.Geocoder(),
        };
      })
      .catch(() => {
        if (alive.current) setFailed(true);
      });
  }, [armed]);

  const commit = useCallback(
    (place) => {
      const params = placeToParams(place);
      if (!params) {
        setError("We could not place that address on the map.");
        return;
      }
      setOpen(false);
      /* `address_searched` at the commit, before the navigation or lookup it
         starts: what was typed and what it resolved to, never coordinates
         (that is `lookup_served`'s job). */
      track(
        "address_searched",
        {
          surface: SURFACE[variant] ?? SURFACE.app,
          raw_input: value.trim().slice(0, 500),
          resolved_formatted_address: place.formatted_address
            ? String(place.formatted_address).slice(0, 500)
            : null,
          has_place_id: Boolean(place.place_id),
        },
        { region: stateOf(place) },
      );
      /* Validated first either way: a caller handling its own commit should
         still never be handed a place we could not turn into coordinates. */
      if (onCommit) {
        onCommit(place);
        return;
      }
      navigate(`/app?${params.toString()}`);
    },
    [navigate, onCommit, variant, value],
  );

  /**
   * Prediction → coordinates, by place_id.
   *
   * Geocoding the id rather than calling Place Details: both resolve it, and
   * geocoding is the cheaper of the two by a wide margin on Google's own price
   * list. Details would only earn its cost for fields we do not use.
   */
  const resolve = useCallback(
    async (prediction) => {
      const services = servicesRef.current;
      if (!services) return;
      setPending(true);
      setError(null);
      try {
        const { results } = await services.geocoder.geocode({
          placeId: prediction.place_id,
        });
        if (!alive.current) return;
        if (results?.[0]) commit(results[0]);
        else setError("We could not place that address on the map.");
      } catch {
        if (alive.current) setError("We could not place that address on the map.");
      } finally {
        if (alive.current) setPending(false);
      }
    },
    [commit],
  );

  /* Typed text, debounced. `requestSeq` drops answers that arrive out of
     order: a slow response for "12 M" must not replace the list for
     "12 Main St". */
  useEffect(() => {
    const text = value.trim();
    if (!armed || text.length < MIN_QUERY) {
      setPredictions([]);
      return undefined;
    }

    const seq = ++requestSeq.current;
    const timer = setTimeout(() => {
      const services = servicesRef.current;
      if (!services) return;
      services.autocomplete.getPlacePredictions(
        { input: text, types: ["address"], ...US_ONLY },
        (results) => {
          if (!alive.current || seq !== requestSeq.current) return;
          setPredictions(results || []);
          setActive(-1);
        },
      );
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [value, armed]);

  const submit = async () => {
    if (pending) return;

    // Something is highlighted in the list — that is the answer.
    if (open && active >= 0 && predictions[active]) {
      resolve(predictions[active]);
      return;
    }

    const text = value.trim();
    if (!text) {
      inputRef.current?.focus();
      return;
    }
    /* Nothing to geocode with yet, or ever. Focusing is the honest response:
       the field is either about to start working or broken on our side, and
       "we could not find that address" would blame the address for neither. */
    if (!servicesRef.current) {
      inputRef.current?.focus();
      return;
    }

    setPending(true);
    setError(null);
    try {
      const { results } = await servicesRef.current.geocoder.geocode({
        address: text,
        ...US_ONLY,
      });
      if (!alive.current) return;
      if (results?.[0]) commit(results[0]);
      else setError("We could not find that address. Try including the city and state.");
    } catch {
      // ZERO_RESULTS rejects rather than resolving empty. Either way the
      // visitor's next move is the same, so both say the same thing.
      if (alive.current) {
        setError("We could not find that address. Try including the city and state.");
      }
    } finally {
      if (alive.current) setPending(false);
    }
  };

  const showList = open && predictions.length > 0;

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
      return;
    }
    if (!showList) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % predictions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i <= 0 ? predictions.length - 1 : i - 1));
    }
  };

  /* No key configured, or the script will not load — no predictions, no
     geocoding, nothing this field can do. A marketing page becomes the link it
     used to be, so the way into the app survives; there is nowhere to send an
     app surface, so it says so rather than showing a dead box. */
  if (!GOOGLE_MAPS_API_KEY || failed) {
    if (!onMarketing) {
      return (
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
          Address search is unavailable right now.
        </p>
      );
    }
    return (
      <Link
        to="/app"
        aria-label="Check your water quality — opens the address search"
        className="group flex w-full max-w-[600px] items-center justify-between overflow-hidden rounded-lg bg-white shadow-sm"
      >
        <span
          aria-hidden="true"
          className="flex items-center gap-3 px-4 py-4 text-zp-slate sm:px-5"
        >
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="text-[15px] sm:text-[17px]">Enter your street address</span>
        </span>
        <span
          aria-hidden="true"
          className="flex shrink-0 items-center gap-2 self-stretch bg-zp-ink px-4 font-mono text-[10px] uppercase tracking-[0.14em] text-white transition-colors duration-150 ease-in group-hover:bg-zp-link sm:px-5 sm:text-[10.5px]"
        >
          <span className="hidden sm:inline">{action}</span>
          <ArrowRight className={`h-3.5 w-3.5 ${ARROW_NUDGE}`} />
        </span>
      </Link>
    );
  }

  return (
    <div className={cn("group/lookup relative w-full", chrome.wrap, className)}>
      <form
        // Enter submits, which is what anyone who typed an address expects. The
        // button and the key press run the same handler.
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className={cn(
          "flex w-full items-stretch overflow-hidden",
          chrome.form,
          // The panel joins the field while it is open, so the two read as one
          // object rather than a box with a second box under it.
          showList && "rounded-b-none",
        )}
      >
        {/* `min-w-0` alongside `flex-1` because a flex item's default
            `min-width: auto` floors it at the input's intrinsic width, which
            would push the button past the border on a narrow viewport. */}
        <div className="relative min-w-0 flex-1">
          <MapPin
            className={cn(
              "pointer-events-none absolute top-1/2 -translate-y-1/2",
              chrome.icon,
            )}
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            value={value}
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              showList && active >= 0 ? `${listId}-${active}` : undefined
            }
            autoComplete="off"
            onFocus={() => {
              setArmed(true);
              setOpen(true);
            }}
            /* Focus leaving is the same intent as a click outside, and it is
               what a keyboard reader does. Deferred, so a pointer landing on
               an option is not cancelled by this firing first. */
            onBlur={() => setTimeout(() => alive.current && setOpen(false), 120)}
            onKeyDown={handleKeyDown}
            onChange={(e) => {
              setValue(e.target.value);
              setOpen(true);
              if (error) setError(null);
            }}
            placeholder={chrome.placeholder}
            aria-label="Enter a street address to check its water"
            aria-describedby={notice ? noticeId : undefined}
            autoFocus={autoFocus}
            data-testid="address-lookup-input"
            className={cn("w-full bg-transparent focus:outline-none", chrome.input)}
          />
        </div>

        <button
          type="submit"
          // A pointer heading for the button is someone about to search, so
          // arming here starts the SDK loading before the click lands.
          onMouseEnter={() => setArmed(true)}
          disabled={pending}
          className={cn(
            "group flex border shrink-0 items-center gap-2 self-stretch font-mono text-[10px] uppercase tracking-[0.14em] transition-colors sm:text-[10.5px]",
            "bg-zp-ink border-zp-ink text-white disabled:opacity-70 hover:bg-zp-link hover:border-zp-link",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/60",
            chrome.button,
          )}
        >
          {pending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              <span className="sr-only">Looking up that address…</span>
            </>
          ) : (
            <>
              {/* Never dropped, only hidden: the arrow is the affordance and
                  this is the button's name. */}
              <span className={chrome.label}>{action}</span>
              <ArrowRight className={`h-3.5 w-3.5 ${ARROW_NUDGE}`} aria-hidden="true" />
            </>
          )}
        </button>
      </form>

      {/* Our list, in the site's own type. Absolutely positioned so arriving
          predictions do not push the page around. */}
      {showList && (
        <div
          className={cn(
            "absolute left-0 right-0 top-full z-40 overflow-hidden rounded-b-lg border border-t-0 shadow-lg",
            "border-zp-ink/[0.12] bg-white dark:border-border dark:bg-card",
          )}
        >
          <ul id={listId} role="listbox" aria-label="Address suggestions">
            {predictions.map((p, i) => {
              const main = p.structured_formatting?.main_text || p.description;
              const rest = p.structured_formatting?.secondary_text;
              return (
                <li
                  key={p.place_id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  /* Pointer down rather than click: blur fires first on a
                     click and would close the list from under it. */
                  onMouseDown={(e) => {
                    e.preventDefault();
                    resolve(p);
                  }}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "cursor-pointer border-t px-4 py-2.5 first:border-t-0",
                    "border-zp-ink/[0.07] dark:border-border",
                    i === active ? "bg-zp-surface dark:bg-muted/50" : "bg-transparent",
                  )}
                >
                  <span className="block truncate text-[13.5px] text-zp-ink dark:text-foreground">
                    {main}
                  </span>
                  {rest && (
                    <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-[0.1em] text-zp-slate dark:text-muted-foreground">
                      {rest}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {/* Required by the Places terms wherever predictions are shown
              without a Google map. Set in the site's own micro-caps rather
              than as Google's bitmap: the terms ask for the credit, and this
              is the credit. */}
          <p className="border-t border-zp-ink/[0.07] bg-zp-surface px-4 py-2 font-mono text-[9px] uppercase tracking-[0.14em] text-zp-slate dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
            Powered by Google
          </p>
        </div>
      )}

      {/* Live, because it answers an action the visitor just took. */}
      {error && (
        <p
          role="status"
          className={cn(
            "mt-2.5 font-mono text-[10.5px] uppercase tracking-[0.12em]",
            chrome.error,
          )}
        >
          {error}
        </p>
      )}

      {/* Notice, not consent: nothing to tick, nothing in the way. Part of the
          input's description, so it is read with the field. */}
      {notice && (
        <p
          id={noticeId}
          data-testid="search-notice"
          className={cn("text-[12px] leading-snug", chrome.notice)}
        >
          {SEARCH_NOTICE}{" "}
          <Link
            to={SEARCH_NOTICE_HREF}
            /* Keeps the field focused through the press, so the console's
               focus-shown notice does not vanish between pointer-down and
               the click that follows it. */
            onMouseDown={(e) => e.preventDefault()}
            className={cn("underline underline-offset-2", chrome.noticeLink)}
          >
            Privacy Policy
          </Link>
        </p>
      )}
    </div>
  );
};

export default AddressLookup;
